import { Typography } from 'antd'
import { observer } from 'mobx-react-lite'

import { Form, FormField } from '@/Core/UI/Form'
import { OfficeFileActions } from '@/Ship/Components/OfficeViewer'
import {
    EntityBody,
    EntityHeader,
    EntityHero,
    EntityLayout,
    FieldCell,
    FieldGrid,
    FieldSection,
    FileFieldCell,
    SectionEmpty,
} from '@/Ship/Components/EntityCard'

import { DocumentLetterAction } from '../Letters'
import { CRM_DOCUMENT_FIELD_LABELS as L, CRM_DOCUMENT_FILE_FIELDS } from './CrmDocument'
import { ReloadCrmDocumentFilesTask } from './ReloadCrmDocumentFilesTask'

import type { CrmDocumentFilesFormContext } from './ReloadCrmDocumentFilesTask'

import type { ReactNode } from 'react'

const { Text } = Typography

/** Те, що картка документа бере з ядрового `formContext`. */
export interface CrmDocumentFormContextLike {
    isEditable?: boolean
    values?: {
        uuid?: unknown
        title?: unknown
        wordFile?: unknown
        _permissions?: { update?: boolean }
        [key: string]: unknown
    }
}

interface CrudContextLike {
    resource?: { key?: string; restUrl?: unknown; fields?: Record<string, unknown> }
}

interface TemplateProps {
    crudContext: CrudContextLike | undefined
    formContext: CrmDocumentFormContextLike | undefined
}

/**
 * Блоки картки документа (`SPEC-009` §8.5): що це за документ, його файли,
 * службова інформація. «Назва» — лише перегляд: її рахує бек із номера й дати
 * при кожному збереженні (КЛ-28), тож у формі створення її немає.
 */
const SECTIONS = {
    document: ['documentType', 'title', 'number', 'date'],
    service: ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
} as const

/** Ставить сервер — у формі нового документа їм нема чого показати. */
const SERVER_FIELDS = new Set<string>(['title', ...SECTIONS.service])

const PLACED = new Set<string>([...Object.values(SECTIONS).flat(), ...CRM_DOCUMENT_FILE_FIELDS, 'id'])

const hasFile = (value: unknown): boolean =>
    Array.isArray(value) ? value.length > 0 : value !== null && typeof value === 'object'

/**
 * Шапка — назва документа, праворуч — кнопка листа документа (`SPEC-013`
 * §9.5, S8b). Окремий observer: шаблон значень не читає (`SPEC-002` §4.2).
 */
const CrmDocumentCardHeader = observer(
    ({ formContext, aside }: { formContext: CrmDocumentFormContextLike; aside: ReactNode }) => {
        const title = typeof formContext.values?.title === 'string' ? formContext.values.title.trim() : ''

        return <EntityHeader {...(title !== '' ? { title } : {})} aside={aside} />
    }
)

/** Файл поля: об'єкт або перший елемент масиву; немає — `null`. */
const fileOf = (value: unknown): { originalName?: unknown } | null => {
    const file = Array.isArray(value) ? value[0] : value

    return file !== null && typeof file === 'object' ? (file as { originalName?: unknown }) : null
}

/**
 * «Переглянути» / «Редагувати» в Collabora біля файлу збереженого документа
 * (`SPEC-012` §7.10, §9.2). Редагувати — лише Word і за `_permissions.update`
 * документа (= право змінювати власника); після закриття редактора файли
 * перечитуються — бек уже перегенерував PDF (P-33, P-37).
 */
const CrmDocumentOfficeActions = observer(
    ({ formContext, restUrl, field }: { formContext: CrmDocumentFormContextLike; restUrl: string; field: 'wordFile' | 'pdfFile' }) => {
        const uuid = formContext.values?.uuid
        const file = fileOf(formContext.values?.[field])

        if (typeof uuid !== 'string' || uuid === '' || file === null || restUrl === '') return null

        return (
            <OfficeFileActions
                sessionUrl={`${restUrl}/${encodeURIComponent(uuid)}/edit_session`}
                kind={field === 'wordFile' ? 'word' : 'pdf'}
                fileName={typeof file.originalName === 'string' ? file.originalName : L[field]}
                canEdit={formContext.values?._permissions?.update === true}
                onClosed={() => {
                    void ReloadCrmDocumentFilesTask(restUrl, formContext as CrmDocumentFilesFormContext).catch(() => undefined)
                }}
            />
        )
    }
)

/**
 * «Pdf файл»: вручну — лише поки немає Word (`SPEC-008` §5.7.2, КЛ-30). Коли
 * Word є, PDF формує з нього бек, і поле лише показує файл — навіть у формі.
 * Окремий observer: читає `wordFile`, який змінюється завантаженням.
 */
const CrmDocumentPdfCell = observer(({ formContext, restUrl }: { formContext: CrmDocumentFormContextLike; restUrl: string }) => {
    const isEditable = formContext.isEditable === true
    const fromWord = hasFile(formContext.values?.wordFile)

    if (isEditable && !fromWord) return <FormField name="pdfFile" />

    return (
        <>
            <FileFieldCell formContext={{ values: formContext.values ?? {} }} name="pdfFile" label={L.pdfFile} />
            <CrmDocumentOfficeActions formContext={formContext} restUrl={restUrl} field="pdfFile" />
            {isEditable ? <Text type="secondary">PDF формується з Word автоматично.</Text> : null}
        </>
    )
})

/**
 * Картка документа клієнта чи перевізника (`SPEC-008` §7.1, `SPEC-009` §8.5):
 * «Тип документа»*, «Назва», «Номер документу», «Дата документа», «Word файл»,
 * «Pdf файл»; «Службова інформація».
 *
 * Файли вантажаться одним кроком у **збережений** документ (`SPEC-008`
 * §5.7.1), тож у формі створення замість них — пояснення. Кнопка листа
 * документа (S8b, `SPEC-013` §9.5) — у шапці збереженого документа; вміст —
 * `LetterContainer/Hooks`. Біля файлів — перегляд і редагування в Collabora
 * (S7b, Ship `OfficeFileActions`, `SPEC-012` §7.10).
 *
 * Шаблон один на обох власників. Поля власника в ресурсі немає: його задає
 * вкладка при створенні, і змінити його не можна (`SPEC-008` §5.1).
 */
export const CrmDocumentCardTemplate = observer(({ crudContext, formContext }: TemplateProps) => {
    const fields = crudContext?.resource?.fields ?? {}
    const uuid = formContext?.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''
    const restUrl = typeof crudContext?.resource?.restUrl === 'string' ? crudContext.resource.restUrl : ''

    // Поля, додані в ресурс пізніше, не мають зникати мовчки
    const rest = Object.keys(fields).filter((name) => !PLACED.has(name))

    const cell = (name: string): ReactNode =>
        name in fields && !(isNew && SERVER_FIELDS.has(name)) ? (
            <FieldCell key={name}>
                <FormField name={name} />
            </FieldCell>
        ) : null

    const grid = (names: readonly string[]) => (
        <Form formContext={formContext}>
            <FieldGrid>{names.map(cell)}</FieldGrid>
        </Form>
    )

    const files =
        isNew || !formContext ? (
            <SectionEmpty>Word і PDF можна буде завантажити після збереження документа.</SectionEmpty>
        ) : (
            <Form formContext={formContext}>
                <FieldGrid>
                    {'wordFile' in fields ? (
                        <FieldCell wide>
                            <FileFieldCell formContext={formContext} name="wordFile" label={L.wordFile} />
                            <CrmDocumentOfficeActions formContext={formContext} restUrl={restUrl} field="wordFile" />
                        </FieldCell>
                    ) : null}
                    {'pdfFile' in fields ? (
                        <FieldCell wide>
                            <CrmDocumentPdfCell formContext={formContext} restUrl={restUrl} />
                        </FieldCell>
                    ) : null}
                </FieldGrid>
            </Form>
        )

    const main = (
        <>
            <FieldSection title="Документ">{grid([...SECTIONS.document, ...rest])}</FieldSection>
            <FieldSection title="Файли">{files}</FieldSection>
            <FieldSection title="Службова інформація" hidden={isNew}>
                {grid(SECTIONS.service)}
            </FieldSection>
        </>
    )

    return (
        <EntityLayout>
            {!isNew && formContext ? (
                <EntityHero>
                    <CrmDocumentCardHeader
                        formContext={formContext}
                        aside={<DocumentLetterAction documentResource={crudContext?.resource?.key} record={{ uuid }} />}
                    />
                </EntityHero>
            ) : null}
            <EntityBody main={main} />
        </EntityLayout>
    )
})
